import {useEffect, useReducer} from "react";

import {IRPStore} from "../../app/rp_reducer";
import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {useInitialLeadFormValues} from "../../lead/hooks/use_initial_lead_form_values";
import {useLeadData} from "../../lead/hooks/use_lead_data";
import {useLeadRequiredParams} from "../../lead/hooks/use_lead_required_params";
import {usePriceConversationPrice} from "../../lead/hooks/use_price_conversation_price";
import {ISentLeadActions, setDidSubmitOnMountAction} from "../../lead/reducers/lead_form_reducer";
import {resetMainLeadData, setLeadModalMainLeadType} from "../../lead/state/main_lead_slice";
import {ILeadModalLeadType} from "../../lead/types/ILeadModalLeadType";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {initialStepState, ModalStepActionTypes, modalStepReducer} from "../reducers/modal_step_reducer";
import {LeadModalStep} from "../types/LeadModalStep";
import {ApplicationSource, ApplicationSourceSection} from "../utils/ApplicationSource";
import {getStoreHash} from "../utils/get_store_hash";

export const useApplicationModalArray = () => {
    const dispatch = useAppDispatch();

    const {vendorId, offerId, propertyId} = useLeadRequiredParams();
    const {isPriceAvailable, price} = usePriceConversationPrice();
    const parametrizedStoreHash = getStoreHash({
        vendorId,
        offerId,
        propertyId
    });

    const {isModalOpen, source, sourceSection, customFormText, initialStep, forceClose} = useAppSelector((state) => state.mainLead);
    const isAutoresponderEnabled = useAppSelector(
        (state: IRPStore) => state.application[parametrizedStoreHash]?.offer?.detail?.configuration.is_autoresponder_enabled
    );

    const leadData = useLeadData({
        storeHash: parametrizedStoreHash,
        vendorId,
        offerId: offerId || undefined,
        propertyId: propertyId || undefined,
        skipFetch: !isModalOpen
    });

    const initialLeadFormValues = useInitialLeadFormValues({
        leadData,
        realEstateIds: {offerId: offerId || undefined, propertyId: propertyId || undefined, vendorId},
        resetValues: !isModalOpen,
        customFormText: customFormText || undefined
    });

    const isLeadDataLoading = !initialLeadFormValues || !leadData;
    const isAbroad = leadData?.offer?.detail?.region.country && leadData?.offer?.detail?.region.country !== 1; // Country.POLAND = 1

    const [modalStepState, dispatchModalStepState] = useReducer(modalStepReducer, {
        ...initialStepState,
        ...(initialStep && {step: initialStep}),
        isAutoresponderEnabled: !!isAutoresponderEnabled,
        forceClose: !!forceClose
    });

    /**
     * effect to manage the initial step when opening the modal if no initialStep is provided
     */
    useEffect(() => {
        const isPriceAvailabilityMissing = isPriceAvailable === null;

        if (!isModalOpen || isPriceAvailabilityMissing || isLeadDataLoading) {
            return;
        }

        const conversationLeadAvailable = isPriceAvailable && propertyId;
        const isGeneratioSuccessInfoScenario = initialStep === LeadModalStep.MULTILEAD;
        const shouldUseFullOfferForm = propertyId && !isAbroad;
        const isSimpleFormStep = initialStep === LeadModalStep.APPLICATION_FORM_FULL_OFFER || initialStep === LeadModalStep.APPLICATION_FORM_WITH_NEGOTIATION;
        const isSidebarForm = initialStep === LeadModalStep.MULTILEAD && sourceSection === ApplicationSourceSection.SIDEBAR;

        let mainLeadType: ILeadModalLeadType;
        if (shouldUseFullOfferForm || isSimpleFormStep) {
            mainLeadType = "simple";
        } else if (conversationLeadAvailable && !isGeneratioSuccessInfoScenario) {
            mainLeadType = "conversation";
        } else if (isSidebarForm) {
            mainLeadType = "sidebar_form";
        } else {
            mainLeadType = "standard";
        }

        dispatch(setLeadModalMainLeadType(mainLeadType));

        if (initialStep) {
            return;
        }

        if (shouldUseFullOfferForm) {
            dispatchModalStepState({
                type: ModalStepActionTypes.OPEN,
                payload: {step: LeadModalStep.APPLICATION_FORM_FULL_OFFER}
            });
        } else {
            dispatchModalStepState({
                type: ModalStepActionTypes.OPEN,
                payload: {
                    step: mainLeadType === "conversation" ? LeadModalStep.APPLICATION_FORM_WITH_CONVERSATION : LeadModalStep.APPLICATION_FORM_STANDARD
                }
            });
        }
    }, [isModalOpen, isPriceAvailable, isLeadDataLoading]);

    /**
     * effect to close the modal when the step is set to close
     */
    useEffect(() => {
        if (modalStepState.step === LeadModalStep.CLOSE) {
            dispatch(resetMainLeadData());
            dispatch<ISentLeadActions>(setDidSubmitOnMountAction(false));
        }
    }, [modalStepState.step]);

    /**
     * effect to update the isAutoresponderEnabled value in the modal step state
     */
    useEffect(() => {
        // `isAutoresponderEnabled` is value from API, so we need to update this value in reducer state
        if (isModalOpen && isAutoresponderEnabled) {
            dispatchModalStepState({type: ModalStepActionTypes.SET_IS_AUTORESPONDER_ENABLED, payload: {isAutoresponderEnabled}});
        }
    }, [isAutoresponderEnabled, isModalOpen]);

    /**
     * effect to update the lead data in the modal step state
     */
    useEffect(() => {
        if (leadData) {
            dispatchModalStepState({type: ModalStepActionTypes.UPDATE_LEAD_DATA, payload: leadData});
        }
    }, [leadData, isModalOpen]);

    /**
     * effect to verify that `source` and `sourceSection` are defined
     */
    useEffect(() => {
        if (!source || !sourceSection) {
            notifyBugsnag("source or sourceSection is undefined", "useApplicationModalArray - source verification");
        }
    }, [source, sourceSection]);

    return {
        isOpened: isModalOpen,
        source: source as ApplicationSource,
        sourceSection: sourceSection as ApplicationSourceSection,
        modalStepState,
        dispatchModalStepState,
        parametrizedStoreHash,
        leadData,
        initialLeadFormValues,
        customFormText,
        price
    };
};
import {useEffect, useReducer} from "react";

import {IRPStore} from "../../app/rp_reducer";
import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {useInitialLeadFormValues} from "../../lead/hooks/use_initial_lead_form_values";
import {useLeadData} from "../../lead/hooks/use_lead_data";
import {useLeadRequiredParams} from "../../lead/hooks/use_lead_required_params";
import {usePriceConversationPrice} from "../../lead/hooks/use_price_conversation_price";
import {ISentLeadActions, setDidSubmitOnMountAction} from "../../lead/reducers/lead_form_reducer";
import {resetMainLeadData, setLeadModalMainLeadType} from "../../lead/state/main_lead_slice";
import {ILeadModalLeadType} from "../../lead/types/ILeadModalLeadType";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {initialStepState, ModalStepActionTypes, modalStepReducer} from "../reducers/modal_step_reducer";
import {LeadModalStep} from "../types/LeadModalStep";
import {ApplicationSource, ApplicationSourceSection} from "../utils/ApplicationSource";
import {getStoreHash} from "../utils/get_store_hash";

export const useApplicationModalArray = () => {
    const dispatch = useAppDispatch();

    const {vendorId, offerId, propertyId} = useLeadRequiredParams();
    const {isPriceAvailable, price} = usePriceConversationPrice();
    const parametrizedStoreHash = getStoreHash({
        vendorId,
        offerId,
        propertyId
    });

    const {isModalOpen, source, sourceSection, customFormText, initialStep, forceClose} = useAppSelector((state) => state.mainLead);
    const isAutoresponderEnabled = useAppSelector(
        (state: IRPStore) => state.application[parametrizedStoreHash]?.offer?.detail?.configuration.is_autoresponder_enabled
    );

    const leadData = useLeadData({
        storeHash: parametrizedStoreHash,
        vendorId,
        offerId: offerId || undefined,
        propertyId: propertyId || undefined,
        skipFetch: !isModalOpen
    });

    const initialLeadFormValues = useInitialLeadFormValues({
        leadData,
        realEstateIds: {offerId: offerId || undefined, propertyId: propertyId || undefined, vendorId},
        resetValues: !isModalOpen,
        customFormText: customFormText || undefined
    });

    const isLeadDataLoading = !initialLeadFormValues || !leadData;
    const isAbroad = leadData?.offer?.detail?.region.country && leadData?.offer?.detail?.region.country !== 1; // Country.POLAND = 1

    const [modalStepState, dispatchModalStepState] = useReducer(modalStepReducer, {
        ...initialStepState,
        ...(initialStep && {step: initialStep}),
        isAutoresponderEnabled: !!isAutoresponderEnabled,
        forceClose: !!forceClose
    });

    /**
     * effect to manage the initial step when opening the modal if no initialStep is provided
     */
    useEffect(() => {
        const isPriceAvailabilityMissing = isPriceAvailable === null;

        if (!isModalOpen || isPriceAvailabilityMissing || isLeadDataLoading) {
            return;
        }

        const conversationLeadAvailable = isPriceAvailable && propertyId;
        const isGeneratioSuccessInfoScenario = initialStep === LeadModalStep.MULTILEAD;
        const shouldUseFullOfferForm = propertyId && !isAbroad;
        const isSimpleFormStep = initialStep === LeadModalStep.APPLICATION_FORM_FULL_OFFER || initialStep === LeadModalStep.APPLICATION_FORM_WITH_NEGOTIATION;
        const isSidebarForm = initialStep === LeadModalStep.MULTILEAD && sourceSection === ApplicationSourceSection.SIDEBAR;

        let mainLeadType: ILeadModalLeadType;
        if (shouldUseFullOfferForm || isSimpleFormStep) {
            mainLeadType = "simple";
        } else if (conversationLeadAvailable && !isGeneratioSuccessInfoScenario) {
            mainLeadType = "conversation";
        } else if (isSidebarForm) {
            mainLeadType = "sidebar_form";
        } else {
            mainLeadType = "standard";
        }

        dispatch(setLeadModalMainLeadType(mainLeadType));

        if (initialStep) {
            return;
        }

        if (shouldUseFullOfferForm) {
            dispatchModalStepState({
                type: ModalStepActionTypes.OPEN,
                payload: {step: LeadModalStep.APPLICATION_FORM_FULL_OFFER}
            });
        } else {
            dispatchModalStepState({
                type: ModalStepActionTypes.OPEN,
                payload: {
                    step: mainLeadType === "conversation" ? LeadModalStep.APPLICATION_FORM_WITH_CONVERSATION : LeadModalStep.APPLICATION_FORM_STANDARD
                }
            });
        }
    }, [isModalOpen, isPriceAvailable, isLeadDataLoading]);

    /**
     * effect to close the modal when the step is set to close
     */
    useEffect(() => {
        if (modalStepState.step === LeadModalStep.CLOSE) {
            dispatch(resetMainLeadData());
            dispatch<ISentLeadActions>(setDidSubmitOnMountAction(false));
        }
    }, [modalStepState.step]);

    /**
     * effect to update the isAutoresponderEnabled value in the modal step state
     */
    useEffect(() => {
        // `isAutoresponderEnabled` is value from API, so we need to update this value in reducer state
        if (isModalOpen && isAutoresponderEnabled) {
            dispatchModalStepState({type: ModalStepActionTypes.SET_IS_AUTORESPONDER_ENABLED, payload: {isAutoresponderEnabled}});
        }
    }, [isAutoresponderEnabled, isModalOpen]);

    /**
     * effect to update the lead data in the modal step state
     */
    useEffect(() => {
        if (leadData) {
            dispatchModalStepState({type: ModalStepActionTypes.UPDATE_LEAD_DATA, payload: leadData});
        }
    }, [leadData, isModalOpen]);

    /**
     * effect to verify that `source` and `sourceSection` are defined
     */
    useEffect(() => {
        if (!source || !sourceSection) {
            notifyBugsnag("source or sourceSection is undefined", "useApplicationModalArray - source verification");
        }
    }, [source, sourceSection]);

    return {
        isOpened: isModalOpen,
        source: source as ApplicationSource,
        sourceSection: sourceSection as ApplicationSourceSection,
        modalStepState,
        dispatchModalStepState,
        parametrizedStoreHash,
        leadData,
        initialLeadFormValues,
        customFormText,
        price
    };
};
